import { ConfigRegistryType, ConfigRegistryConfig } from 'types/apis/pilotInterfaces';

/**
 * Allowed component names to process from registry
 */
export const ALLOWED_COMPONENTS: ReadonlySet<string> = new Set([
  'Aggregation',
  'Disaggregation',
  'Ingestion',
]);

/**
 * Filter registry entries to only include configs whose component is in the allowed list
 * Handles component as a single value or comma-separated items (e.g., "Aggregation,Disaggregation" or "Aggregation")
 */
export function filterRegistryByComponents(
  registry: ConfigRegistryType[],
  allowedComponents: ReadonlySet<string> = ALLOWED_COMPONENTS
): ConfigRegistryType[] {
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => {
        const component = (cfg as any).component;
        if (!component) {
          return false;
        }
        
        // If component is a string, split by comma and check if any match
        if (typeof component === 'string') {
          const components = component
            .split(',')
            .map((c: string) => c.trim())
            .filter(Boolean);
          
          // Check if any of the split components are in the allowed set
          return components.some((comp: string) => allowedComponents.has(comp));
        }
        
        // Fallback: check if the component value itself is in the allowed set
        return allowedComponents.has(String(component));
      })
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Process config value based on its data type and metadata
 * @param value - The raw value from the form
 * @param configValueMetadata - The config value metadata object
 * @returns Processed string value ready for saving
 */
export function processConfigValue(value: any, configValueMetadata: any): string {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return '';
  }

  // Handle empty string
  if (value === '') {
    return '';
  }

  // Extract metadata from configValueMetadata object
  const metadata = configValueMetadata || {};
  
  // If metadata object is empty, return string value directly
  if (!metadata || Object.keys(metadata).length === 0) {
    return String(value);
  }
  
  const dataType = metadata?.dataType || 'TEXT';

  // Only handle TEXT and ARRAY data types
  if (dataType === 'TEXT') {
    // Case 1: If it's an array of strings and dataType is TEXT, make it comma separated string
    if (Array.isArray(value)) {
      return value.join(',');
    }
    // Case 2: If it's a JSON array string and dataType is TEXT, convert to comma separated string
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.join(',');
        }
      } catch {
        // If parsing fails, return as is
      }
    }
    // For other values, convert to string
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  if (dataType === 'ARRAY') {
    // Case 2: If it's a comma separated string and dataType is ARRAY, make it array of strings
    if (typeof value === 'string' && !value.startsWith('[') && !value.startsWith('{')) {
      // Split by comma and trim each element
      const arrayValue = value.split(',').map(item => item.trim());
      return JSON.stringify(arrayValue);
    }
    // For arrays, stringify them
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    // For JSON strings (starting with [ or {), keep as is
    if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
      return value;
    }
    // For other values, wrap in array
    return JSON.stringify([value]);
  }

  // Default fallback
  return String(value);
}

/**
 * Returns true if the given config entry is marked as a duplicate (per new or legacy schema).
 */
export function isDuplicateConfig(config: ConfigRegistryConfig | any): boolean {
  const dup = (config as any)?.duplicateConfig;
  
  // New pattern: array of objects with configKey and configType
  if (Array.isArray(dup) && dup.length > 0) {
    // Check if it's the new pattern (array of objects with configKey and configType)
    if (dup[0] && typeof dup[0] === 'object' && 'configKey' in dup[0] && 'configType' in dup[0]) {
      return dup.length > 0;
    }
    // Legacy pattern: array of strings
    return dup.length > 0;
  }
  return false;
}

/**
 * Returns a deep-copied registry where any configs marked as duplicates are removed from each type.
 */
export function stripDuplicateConfigs(registry: ConfigRegistryType[]): ConfigRegistryType[] {
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => !isDuplicateConfig(cfg))
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Helper to apply an additional predicate while also stripping duplicates.
 */
export function filterConfigsNoDuplicates(
  registry: ConfigRegistryType[],
  predicate: (cfg: any) => boolean
): ConfigRegistryType[] {
  return registry
    .map((configType) => ({
      ...configType,
      configs: configType.configs.filter((cfg: any) => predicate(cfg) && !isDuplicateConfig(cfg))
    }))
    .filter((configType) => configType.configs.length > 0);
}

/**
 * Interface for config group selection state
 */
export interface ConfigGroupSelection {
  [groupKey: string]: string; // normalized groupKey -> selected category
}

/**
 * Normalize a ConfigGroup string to a canonical key (split by '|' or ':', trim, sort, join with '|')
 */
function normalizeGroupKey(groupStr: string): string {
  const opts = groupStr
    .split(/\||:/)
    .map((s) => s.trim())
    .filter(Boolean)
    .sort();
  return opts.join('|');
}

/**
 * Get unique config groups from registry as normalized combined keys
 * Example: returns ["S3_Pilot|SFTP_Pilot"] instead of rendering each option separately
 */
export function getUniqueConfigGroups(registry: ConfigRegistryType[]): string[] {
  const uniqueGroupStrings = new Set<string>();
  
  registry.forEach(configType => {
    configType.configs.forEach(config => {
      const rawGroup: string | null | undefined = (config as any).ConfigGroup ?? (config as any).configGroup;
      if (rawGroup) {
        uniqueGroupStrings.add(normalizeGroupKey(rawGroup));
      }
    });
  });
  
  return Array.from(uniqueGroupStrings).sort();
}

/**
 * Get config group options for a specific (possibly normalized) group key
 */
export function getConfigGroupOptions(registry: ConfigRegistryType[], groupKey: string): string[] {
  // If the provided groupKey already looks like a normalized combo, return its parts directly
  if (groupKey.includes('|') || groupKey.includes(':')) {
    return groupKey
      .split(/\||:/)
      .map(opt => opt.trim())
      .filter(opt => opt)
      .sort();
  }

  // Fallback: discover options by scanning registry for any config that belongs to this single group key
  const options = new Set<string>();
  
  registry.forEach(configType => {
    configType.configs.forEach(config => {
      const rawGroup: string | null | undefined = (config as any).ConfigGroup ?? (config as any).configGroup;
      if (rawGroup) {
        const groupOptions = rawGroup.split('|').map(opt => opt.trim()).filter(opt => opt);
        if (groupOptions.includes(groupKey)) {
          groupOptions.forEach(option => options.add(option));
        }
      }
    });
  });
  
  return Array.from(options).sort();
}

/**
 * Filter configs based on config group selection
 */
export function filterConfigsByGroupSelection(
  registry: ConfigRegistryType[], 
  groupSelection: ConfigGroupSelection
): ConfigRegistryType[] {
  if (Object.keys(groupSelection).length === 0) {
    return registry; // No selection, show all
  }
  
  return registry.map(configType => ({
    ...configType,
    configs: configType.configs.filter(config => {
      // If config has no group, always show it
      const rawGroup: string | null | undefined = (config as any).ConfigGroup ?? (config as any).configGroup;
      if (!rawGroup) {
        return true;
      }
      
      // Check if config belongs to any selected normalized group and matches the selected category
      const normalizedKey = normalizeGroupKey(rawGroup);
      const selectedCategory = groupSelection[normalizedKey];
      const rawCategory: string | null | undefined = (config as any).ConfigGroupCatagory ?? (config as any).configGroupCatagory;
      if (selectedCategory && rawCategory === selectedCategory) {
        return true;
      }
      
      return false;
    })
  }));
}

/**
 * Sort configs by ConfigOrder and ConfigGroupOrder
 */
export function sortConfigsByOrder(registry: ConfigRegistryType[]): ConfigRegistryType[] {
  return registry.map(configType => ({
    ...configType,
    configs: [...configType.configs].sort((a, b) => {
      // First sort by ConfigOrder
      const orderA = (a as any).ConfigOrder ?? (a as any).configOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = (b as any).ConfigOrder ?? (b as any).configOrder ?? Number.MAX_SAFE_INTEGER;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Then sort by ConfigGroupOrder within the same group
      const groupOrderA = (a as any).ConfigGroupOrder ?? (a as any).configGroupOrder ?? Number.MAX_SAFE_INTEGER;
      const groupOrderB = (b as any).ConfigGroupOrder ?? (b as any).configGroupOrder ?? Number.MAX_SAFE_INTEGER;
      
      return groupOrderA - groupOrderB;
    })
  }));
}

/**
 * Get configs that should be saved based on group selection
 */
export function getConfigsToSave(
  registry: ConfigRegistryType[], 
  groupSelection: ConfigGroupSelection
): ConfigRegistryType[] {
  if (Object.keys(groupSelection).length === 0) {
    return registry; // No selection, save all
  }
  
  return registry.map(configType => ({
    ...configType,
    configs: configType.configs.filter(config => {
      // If config has no group, always save it
      const rawGroup: string | null | undefined = (config as any).ConfigGroup ?? (config as any).configGroup;
      if (!rawGroup) {
        return true;
      }
      
      // Only save configs that match the selected category for the normalized group
      const normalizedKey = normalizeGroupKey(rawGroup);
      const selectedCategory = groupSelection[normalizedKey];
      const rawCategory: string | null | undefined = (config as any).ConfigGroupCatagory ?? (config as any).configGroupCatagory;
      if (selectedCategory && rawCategory === selectedCategory) {
        return true;
      }
      
      return false;
    })
  })).filter(configType => configType.configs.length > 0);
} 